import { createRouter, createWebHistory } from "vue-router";
import { DateTime } from "luxon";

import checkUserRole from "@/services/checkUserRole";
import { sessionReady } from "@/services/session";
import { useCompanyStore } from "@/storePinia/company";
import {
  getLocalUser,
  hasManagerAccessToPath,
  isManagerOnly,
} from "@/constants/managerAccess";
import allowedShowDevFeatures from "@/services/allowedShowDevFeatures";

const routes = [
  {
    path: "/",
    name: "/",
    redirect: "/ecommerce/calendar",
  },
  {
    path: "/authentication/logout",
    name: "Вихід",
    component: () => import("@/views/auth/Logout.vue"),
  },
  {
    path: "/ecommerce/calendar",
    name: "Календар",
    component: () =>
      import(/* webpackPrefetch: true */ "@/views/ecommerce/Calendar.vue"),
  },
  {
    path: "/ecommerce/products/products-list",
    name: "Products List",
    component: () => import("@/views/ecommerce/ProductsList.vue"),
  },
  {
    path: "/ecommerce/rooms-prices",
    name: "Ціни номерів",
    component: () => import("@/views/ecommerce/RoomsPrices.vue"),
  },
  {
    path: "/ecommerce/restrictions",
    name: "Заборони",
    component: () => import("@/views/ecommerce/Restrictions.vue"),
  },
  {
    path: "/ecommerce/staff-rates",
    name: "Rates Calendar",
    component: () => import("@/views/ecommerce/StaffRates.vue"),
  },
  {
    path: "/subscription",
    name: "Підписка",
    component: () => import("@/views/ecommerce/Subscription.vue"),
  },
  {
    path: "/subscription/invoice/new",
    name: "Створити рахунок",
    component: () => import("@/views/ecommerce/subscription/InvoiceForm.vue"),
  },
  {
    path: "/subscription/invoice/:id",
    name: "Редагувати рахунок",
    component: () => import("@/views/ecommerce/subscription/InvoiceForm.vue"),
  },
  {
    path: "/service-orders",
    name: "Записи",
    component: () => import("@/views/ecommerce/orders/ServiceOrderList.vue"),
  },
  {
    path: "/service-orders/new",
    name: "new-order",
    component: () =>
      import("@/views/ecommerce/orders/new-service-order/NewServiceOrder.vue"),
  },
  {
    path: "/service-orders/:id",
    name: "Деталі запису",
    component: () =>
      import("@/views/ecommerce/orders/new-service-order/NewServiceOrder.vue"),
  },
  {
    path: "/service-order-invoice/:id",
    name: "service-order-invoice",
    component: () => import("@/views/ecommerce/orders/ServiceOrderInvoice.vue"),
  },
  {
    path: "/certificates",
    name: "Сертифікати",
    component: () => import("@/views/ecommerce/Certificates.vue"),
  },
  {
    path: "/abonements",
    name: "Абонементи",
    component: () => import("@/views/ecommerce/Abonements.vue"),
  },
  {
    path: "/add-abonement",
    name: "Створити абонемент",
    component: () => import("@/views/ecommerce/AddAbonement.vue"),
  },
  {
    path: "/sell-abonement",
    name: "Продати абонемент",
    component: () => import("@/views/ecommerce/SellAbonement.vue"),
  },
  {
    path: "/warehouse/product-orders",
    name: "Продажі",
    component: () =>
      import("@/views/ecommerce/warehouse/productSales/ProductOrderList.vue"),
  },
  {
    path: "/warehouse/orders/new-product-order",
    name: "Створити продаж",
    component: () =>
      import("@/views/ecommerce/warehouse/products/NewProductOrder.vue"),
  },
  {
    path: "/analytics",
    name: "Аналітика",
    component: () => import("@/views/ecommerce/Analytics.vue"),
  },
  {
    path: "/analytics/bystaff",
    component: () => import("@/views/ecommerce/AnalyticsByStaff.vue"),
  },
  {
    path: "/analytics/byserviceorders",
    redirect: "/analytics",
  },
  {
    path: "/analytics/byadditionalservices",
    component: () =>
      import("@/views/ecommerce/AnalyticsByAdditionalServices.vue"),
  },
  {
    path: "/analytics/adr",
    component: () => import("@/views/ecommerce/AnalyticsByADR.vue"),
  },
  {
    path: "/analytics/byadministrators",
    component: () => import("@/views/ecommerce/AnalyticsByAdministrators.vue"),
    meta: {
      requiresOwnerOrRoot: true,
    },
  },
  {
    path: "/clients",
    name: "Клієнти",
    component: () => import("@/views/ecommerce/clients/clientsdatatable.vue"),
  },
  {
    path: "/clients/new",
    name: "Створити клієнта",
    component: () => import("@/views/ecommerce/clients/clientdetails.vue"),
  },
  {
    path: "/clients/:id",
    name: "details_client",
    component: () => import("@/views/ecommerce/clients/clientdetails.vue"),
  },
  {
    path: "/finances/cash",
    name: "Фінанси",
    component: () => import("@/views/pages/finances/cash.vue"),
  },
  {
    path: "/finances/categories",
    name: "Фінанси. Категорії",
    component: () => import("@/views/pages/finances/FinancesCategories.vue"),
  },
  {
    path: "/finances/accounts",
    name: "Фінанси. Рахунки",
    component: () => import("@/views/pages/finances/Accounts.vue"),
  },

  {
    path: "/promocodes/promocodes-overview",
    name: "Промокоди. Огляд",
    component: () => import("@/views/pages/Promocodes/PromocodesOverview.vue"),
  },
  {
    path: "/promocodes/promocodes-history",
    name: "Промокоди. Історія",
    component: () => import("@/views/pages/Promocodes/PromocodesHistory.vue"),
  },
  {
    path: "/promocodes/new-promocode",
    name: "Новий промокод",
    component: () => import("@/views/pages/Promocodes/PromocodesNew.vue"),
  },

  {
    path: "/settings/company",
    name: "Налаштування компанії",
    component: () =>
      import("@/views/pages/settings/Company/CompanyDetails.vue"),
  },
  {
    path: "/team/managers/",
    name: "Адміністратори",
    component: () => import("@/views/pages/settings/Managers/managers.vue"),
  },
  {
    path: "/team/managers/new",
    name: "Створити адміністратора",
    component: () => import("@/views/pages/settings/Managers/manager.vue"),
  },
  {
    path: "/team/managers/:id",
    name: "Редагувати адміністратора",
    component: () => import("@/views/pages/settings/Managers/manager.vue"),
  },
  {
    path: "/settings/positions/new",
    name: "Створити категорію",
    component: () => import("@/views/pages/settings/Positions/NewPosition.vue"),
  },
  {
    path: "/settings/positions/:id",
    name: "Редагувати категорію",
    component: () => import("@/views/pages/settings/Positions/NewPosition.vue"),
  },
  {
    path: "/settings/services",
    name: "Послуги",
    component: () =>
      import("@/views/pages/settings/Services/ServicesDatatable.vue"),
  },
  {
    path: "/settings/additional-services",
    name: "Додаткові послуги",
    component: () =>
      import("@/views/pages/settings/Services/AdditionalServicesDatatable.vue"),
  },
  {
    path: "/settings/additional-services/new",
    name: "Нова додаткова послуга",
    component: () =>
      import("@/views/pages/settings/Services/NewAdditionalService.vue"),
  },
  {
    path: "/settings/additional-services/:id",
    name: "Редагувати додаткову послугу",
    component: () =>
      import("@/views/pages/settings/Services/NewAdditionalService.vue"),
  },
  {
    path: "/settings/services/new",
    name: "Нова послуга",
    component: () => import("@/views/pages/settings/Services/NewService.vue"),
  },
  {
    path: "/settings/services/:id",
    name: "Редагувати послугу",
    component: () => import("@/views/pages/settings/Services/NewService.vue"),
  },
  {
    path: "/settings/staff",
    name: "Майстри",
    component: () => import("@/views/pages/settings/Staff/StaffDatatable.vue"),
  },
  {
    path: "/settings/staff/new",
    name: "Створити майстра",
    component: () => import("@/views/pages/settings/Staff/NewStaff.vue"),
  },
  {
    path: "/settings/staff/:id",
    name: "Редагувати майстра",
    component: () => import("@/views/pages/settings/Staff/NewStaff.vue"),
  },
  {
    path: "/team/salary/",
    name: "Зарплатня",
    component: () => import("@/views/pages/Team/Salary.vue"),
  },
  {
    path: "/team/salary-addschema/",
    name: "Зарплатня - створити схему",
    component: () => import("@/views/pages/Team/SalaryAddSchema.vue"),
  },
  {
    path: "/team/salaryinfo/",
    name: "Зарплатня - деталі",
    component: () => import("@/views/pages/Team/SalaryInfo.vue"),
  },
  {
    path: "/team/salaryinfostaff/",
    name: "Зарплатня - деталі",
    component: () => import("@/views/pages/Team/SalaryInfoStaff.vue"),
  },
  {
    path: "/channel-manager",
    name: "Менеджер каналів - головна",
    component: () =>
      import("@/views/pages/ChannelManager/ChannelManagerMain.vue"),
  },
  {
    path: "/support",
    name: "Допомога",
    component: () => import("@/views/pages/Support.vue"),
  },
  {
    path: "/settings/permissions",
    name: "Доступи",
    component: () => import("@/views/pages/settings/Permissions.vue"),
  },
  {
    path: "/team/workschedule",
    name: "Графік роботи",
    component: () => import("@/views/pages/settings/WorkSchedule.vue"),
  },

  {
    path: "/settings/online-booking",
    name: "Онлайн запис",
    component: () =>
      import("@/views/pages/settings/onlinebooking/OnlineBooking.vue"),
  },
  {
    path: "/spa/online-booking",
    name: "Онлайн запис SPA",
    component: () =>
      import("@/views/pages/settings/onlinebooking/SpaOnlineBooking.vue"),
  },
  {
    path: "/settings/online-checkin",
    name: "Онлайн чек-ін",
    component: () =>
      import("@/views/pages/settings/checkin/CheckinSettings.vue"),
  },
  {
    path: "/settings/online-payment",
    name: "Онлайн оплата",
    component: () => import("@/views/pages/settings/OnlinePayment.vue"),
  },
  {
    path: "/settings/acquiring-accounts/new",
    name: "Додати еквайрінг",
    component: () => import("@/views/pages/settings/AcquiringAccountForm.vue"),
  },
  {
    path: "/settings/acquiring-accounts/:id",
    name: "Редагувати еквайрінг",
    component: () => import("@/views/pages/settings/AcquiringAccountForm.vue"),
  },

  {
    path: "/settings/discounts",
    name: "Знижки",
    component: () => import("@/views/pages/settings/Discounts/Discounts.vue"),
  },
  {
    path: "/settings/discounts/military/new",
    name: "Знижка для військових - створити",
    component: () =>
      import("@/views/pages/settings/Discounts/AddMilitaryDiscount.vue"),
  },
  {
    path: "/settings/discounts/military/:id",
    name: "Знижка для військових - редагувати",
    component: () =>
      import("@/views/pages/settings/Discounts/AddMilitaryDiscount.vue"),
  },
  {
    path: "/settings/discounts/last-minute/new",
    name: "Last minute Знижка - створити",
    component: () =>
      import("@/views/pages/settings/Discounts/AddLastMinuteDiscount.vue"),
  },
  {
    path: "/settings/discounts/last-minute/:id",
    name: "Last minute Знижка - редагувати",
    component: () =>
      import("@/views/pages/settings/Discounts/AddLastMinuteDiscount.vue"),
  },
  {
    path: "/settings/discounts/new",
    name: "Знижки на довгострокове перебування - створити",
    component: () =>
      import("@/views/pages/settings/Discounts/AddLongTermDiscount.vue"),
  },
  {
    path: "/settings/discounts/:id",
    name: "Знижки на довгострокове перебування - редагувати",
    component: () =>
      import("@/views/pages/settings/Discounts/AddLongTermDiscount.vue"),
  },

  {
    path: "/integrations/widget",
    name: "Widget на сайт",
    component: () => import("@/views/pages/Integrations/Widget.vue"),
  },
  {
    path: "/integrations/api",
    name: "API",
    component: () => import("@/views/pages/Integrations/API.vue"),
  },
  {
    path: "/integrations/ai-agent",
    name: "АІ агент",
    component: () => import("@/views/pages/Integrations/AiAgent.vue"),
  },
  {
    path: "/team/editprofile",
    name: "Редагувати профіль",
    component: () => import("@/views/pages/account/EditProfile.vue"),
  },
  {
    path: "/pages/journal",
    name: "Журнал",
    component: () => import("@/views/pages/account/Journal.vue"),
  },
  {
    path: "/reviews/reviews-settings",
    component: () => import("@/views/pages/Reviews/ReviewsSettings.vue"),
  },
  {
    path: "/reviews/reviews-overview",
    name: "ReviewListPage",
    component: () => import("@/views/pages/Reviews/ReviewsOverview.vue"),
  },
  {
    path: "/reviews/edit-review/:feedbackId",
    name: "ReviewPage",
    component: () => import("@/views/pages/Reviews/EditReview.vue"),
    props: true,
  },
  {
    path: "/reviews/spa/edit-review/:feedbackId",
    name: "ReviewPageSpa",
    component: () => import("@/views/pages/Reviews/EditReviewSpa.vue"),
    props: true,
  },

  {
    path: "/warehouse/products-list",
    name: "Товари",
    component: () => import("@/views/ecommerce/ProductsList.vue"),
  },
  {
    path: "/warehouse/suppliers",
    name: "Постачальники",
    component: () =>
      import("@/views/ecommerce/warehouse/suppliers/SuppliersDatatable.vue"),
  },
  {
    path: "/warehouse/new-supplier",
    name: "Створити постачальника",
    component: () =>
      import("@/views/ecommerce/warehouse/suppliers/NewSupplier.vue"),
  },
  {
    path: "/warehouse/supply",
    name: "Створити поставку",
    component: () => import("@/views/ecommerce/warehouse/supply/NewSupply.vue"),
  },
  {
    path: "/warehouse/suppliesdatatable",
    name: "Поставки",
    component: () =>
      import("@/views/ecommerce/warehouse/supply/SuppliesDatatable.vue"),
  },
  {
    path: "/warehouse/newproduct",
    name: "Створити продукт",
    component: () =>
      import("@/views/ecommerce/warehouse/products/NewProduct.vue"),
  },
  {
    path: "/settings/branches",
    name: "Філії",
    component: () => import("@/views/pages/settings/Branches/branches.vue"),
  },
  {
    path: "/settings/branches/new",
    name: "Створити філію",
    component: () => import("@/views/pages/settings/Branches/branch.vue"),
  },
  {
    path: "/settings/branches/:id",
    name: "Редагувати філію",
    component: () => import("@/views/pages/settings/Branches/branch.vue"),
  },
  {
    path: "/integrations/sms",
    name: "Інтеграція SMS",
    component: () => import("@/views/pages/Integrations/SMS.vue"),
  },
  {
    path: "/integrations/email",
    name: "Інтеграція Mailgun",
    component: () => import("@/views/pages/Integrations/Mailgun.vue"),
  },

  {
    path: "/integrations/sms-payment",
    name: "Поповнення SMS",
    component: () => import("@/views/pages/Integrations/SMSpayment.vue"),
  },
  {
    path: "/hotel/onboarding/create-unit",
    name: "Створити кімнату",
    component: () =>
      import("@/views/pages/hotelonboarding/HotelOnboarding.vue"),
  },
  {
    path: "/beauty/onboarding/intro",
    name: "",
    component: () =>
      import("@/views/pages/beautyOnboarding/steps/StepIntro.vue"),
  },
  {
    path: "/beauty/onboarding/welcome",
    name: "",
    component: () =>
      import("@/views/pages/beautyOnboarding/steps/StepWelcome.vue"),
  },
  {
    path: "/beauty/onboarding/add-staff",
    name: "",
    component: () =>
      import("@/views/pages/beautyOnboarding/steps/StepStaff.vue"),
  },
  {
    path: "/beauty/onboarding/add-service",
    name: "",
    component: () =>
      import("@/views/pages/beautyOnboarding/steps/StepService.vue"),
  },
  {
    path: "/beauty/onboarding/success",
    name: "",
    component: () => import("@/views/pages/beautyOnboarding/steps/Success.vue"),
  },

  {
    path: "/integrations/sms-settings",
    name: "Налаштування SMS",
    component: () => import("@/views/pages/Integrations/SMSsettings.vue"),
  },
  {
    path: "/integrations/email/templates",
    name: "Mailgun шаблони",
    component: () => import("@/views/pages/Integrations/MailgunTemplates.vue"),
  },
  {
    path: "/integrations/email/email-settings",
    name: "Налаштування Email",
    component: () => import("@/views/pages/Integrations/MailgunSettings.vue"),
  },
  {
    path: "/integrations/sms-templates",
    name: "Шаблони SMS",
    component: () => import("@/views/pages/Integrations/SMSTemplates.vue"),
  },
  {
    path: "/integrations/sms-manual-sends",
    name: "SMS розсилки",
    component: () => import("@/views/pages/Integrations/SMSManualSends.vue"),
  },
  {
    path: "/integrations/sms-history",
    name: "Історія SMS",
    component: () => import("@/views/pages/Integrations/SMSHistory.vue"),
  },
  {
    path: "/integrations/telegram",
    name: "Інтеграція Telegram",
    component: () => import("@/views/pages/Integrations/Telegram.vue"),
  },
  {
    path: "/spa/resources",
    name: "SPA ресурси",
    component: () => import("@/views/pages/spa/ResourcesDatatable.vue"),
  },
  {
    path: "/spa/resources/new",
    name: "Створити SPA ресурс",
    component: () => import("@/views/pages/spa/SpaResourceForm.vue"),
  },
  {
    path: "/spa/resources/:id",
    name: "Редагувати SPA ресурс",
    component: () => import("@/views/pages/spa/SpaResourceForm.vue"),
  },
  {
    path: "/spa/resource-categories/new",
    name: "Створити SPA категорію",
    component: () => import("@/views/pages/spa/SpaResourceCategoryForm.vue"),
  },
  {
    path: "/spa/resource-categories/:id",
    name: "Редагувати SPA категорію",
    component: () => import("@/views/pages/spa/SpaResourceCategoryForm.vue"),
  },
  {
    path: "/spa/resources-schedule",
    name: "Графік ресурсів",
    component: () => import("@/views/pages/spa/SpaResourcesSchedule.vue"),
  },
  {
    path: "/spa/services",
    name: "SPA послуги",
    component: () => import("@/views/pages/spa/SpaServicesDatatable.vue"),
  },
  {
    path: "/spa/services/new",
    name: "Створити SPA послугу",
    component: () => import("@/views/pages/spa/SpaServiceForm.vue"),
  },
  {
    path: "/spa/services/:id",
    name: "Редагувати SPA послугу",
    component: () => import("@/views/pages/spa/SpaServiceForm.vue"),
  },
  {
    path: "/sellable-services",
    name: "Товари і послуги",
    component: () => import("@/views/ecommerce/services/ServicesDatatable.vue"),
  },
  {
    path: "/sellable-services/sales",
    name: "Продажі (товари і послуги)",
    component: () => import("@/views/ecommerce/sales/SalesDatatable.vue"),
  },
  {
    path: "/sellable-services/sales/new",
    name: "Створення продажу",
    component: () => import("@/views/ecommerce/sales/SaleForm.vue"),
  },
  {
    path: "/sellable-services/sales/:id",
    name: "Редагування продажу",
    component: () => import("@/views/ecommerce/sales/SaleForm.vue"),
  },
  {
    path: "/sellable-services/categories/new",
    name: "Створення категорії товарів і послуг",
    component: () =>
      import("@/views/ecommerce/services/SellableServiceCategoryForm.vue"),
  },
  {
    path: "/sellable-services/categories/:id",
    name: "Редагування категорії товарів і послуг",
    component: () =>
      import("@/views/ecommerce/services/SellableServiceCategoryForm.vue"),
  },
  {
    path: "/sellable-services/new",
    name: "Створення товару і послуги",
    component: () => import("@/views/ecommerce/services/ServiceForm.vue"),
  },
  {
    path: "/sellable-services/:id",
    name: "Редагування товару і послуги",
    component: () => import("@/views/ecommerce/services/ServiceForm.vue"),
  },
  {
    path: "/spa/calendar",
    name: "SPA календар",
    component: () => import("@/views/pages/spa/SpaCalendar.vue"),
  },
  {
    path: "/dashboard",
    name: "Dashboard",
    component: () => import("@/views/pages/Dashboard.vue"),
  },
  {
    path: "/spa/orders",
    name: "SPA записи",
    component: () => import("@/views/pages/spa/orders/SpaOrdersDatatable.vue"),
  },
  {
    path: "/spa/orders/new",
    name: "Створити SPA запис",
    component: () => import("@/views/pages/spa/orders/SpaOrderPage.vue"),
  },
  {
    path: "/spa/orders/:id",
    name: "Редагувати SPA запис",
    component: () => import("@/views/pages/spa/orders/SpaOrderPage.vue"),
  },
  {
    path: "/ig-story-maker",
    component: () => import("@/views/pages/IGStoryMaker.vue"),
  },
  {
    path: "/integrations/poster",
    name: "Poster",
    component: () => import("@/views/pages/Integrations/POSter.vue"),
  },
  {
    path: "/integrations/tips",
    name: "Чайові",
    component: () => import("@/views/pages/Integrations/Tips.vue"),
  },

  {
    path: "/maintenance/migrations",
    name: "maintenance - міграції",
    component: () => import("@/views/pages/maintenance/Migrations.vue"),
    // meta: {
    //   requiresRoot: true,
    // },
  },
  {
    path: "/maintenance/cron",
    name: "maintenance - cron",
    component: () => import("@/views/pages/maintenance/Cron.vue"),
    // meta: {
    //   requiresRoot: true,
    // },
  },
  {
    path: "/maintenance/tenants",
    name: "maintenance - tenants",
    component: () => import("@/views/pages/maintenance/Tenants.vue"),
    // meta: {
    //   requiresRoot: true,
    // },
  },
  {
    path: "/maintenance/tenants-linking",
    name: "maintenance - tenants linking",
    component: () => import("@/views/pages/maintenance/TenantsLinking.vue"),
    // meta: {
    //   requiresRoot: true,
    // },
  },

  {
    path: "/email-confirmation/:tenantName/:confirmationId",
    name: "Підтвердження імейл-адреси",
    component: () => import("@/views/guest/EmailConfirmation.vue"),
    meta: {
      layout: "AuthLayout",
    },
  },

  {
    path: "/:pathMatch(.*)*",
    redirect: "/",
  },
];

// UI Kit routes are only available when VUE_APP_UI_KIT_ENABLED=yes is set in .env
// This is build-time tree-shaking: if the variable is not set, this code will be removed from production bundle
if (process.env.VUE_APP_UI_KIT_ENABLED === "yes") {
  routes.push({
    name: "ui-kit",
    path: "/ui-kit",
    component: () => import("@/views/ui-kit/UIKit.vue"),
    redirect: { name: "ui-kit-select" },
    children: [
      {
        name: "ui-kit-select",
        path: "/ui-kit/select",
        component: () => import("@/views/ui-kit/UIKitSelect.vue"),
      },
      {
        name: "ui-kit-input",
        path: "/ui-kit/input",
        component: () => import("@/views/ui-kit/UIKitInput.vue"),
      },
      {
        name: "ui-kit-data-table",
        path: "/ui-kit/data-table",
        component: () => import("@/views/ui-kit/UIKitDataTable.vue"),
      },
      {
        name: "ui-kit-button",
        path: "/ui-kit/button",
        component: () => import("@/views/ui-kit/UIKitButton.vue"),
      },
      {
        name: "ui-kit-modal",
        path: "/ui-kit/modal",
        component: () => import("@/views/ui-kit/UIKitModal.vue"),
      },
      {
        name: "ui-kit-toast",
        path: "/ui-kit/toast",
        component: () => import("@/views/ui-kit/UIKitToast.vue"),
      },
    ],
  });
}

let forcePaymentEnabledLastCheckAtDate = DateTime.now()
  .minus({ days: 1 })
  .toISODate();

const forcePaymentEnabledCheck = async () => {
  const localUser = getLocalUser();
  const currentDate = DateTime.now().toISODate();
  if (currentDate === forcePaymentEnabledLastCheckAtDate) {
    return;
  }

  const expiredStatuses = ["trialEnded", "waiting"];

  const { company } = useCompanyStore();
  if (expiredStatuses.includes(company.subscriptionStatus)) {
    if (
      isManagerOnly(localUser) &&
      !hasManagerAccessToPath("/subscription", localUser)
    ) {
      forcePaymentEnabledLastCheckAtDate = currentDate;
      return;
    }

    window.location.href = "/subscription";
    return;
  }

  forcePaymentEnabledLastCheckAtDate = currentDate;
};

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  linkActiveClass: "active",
  linkExactActiveClass: "active",
});

router.beforeEach(async () => {
  try {
    await sessionReady();
  } catch (error) {
    console.error("Session bootstrap failed", error);
    return false;
  }
});

router.beforeEach(async (to, from, next) => {
  // if (to.meta?.requiresShowDevFeatures && !allowedShowDevFeatures()) {
  //   return next("/spa");
  // }

  if (to.meta?.requiresRoot) {
    const isOwnerOrRoot = checkUserRole("root");
    if (!isOwnerOrRoot) {
      return next("/ecommerce/calendar");
    }
  }

  if (to.meta?.requiresOwnerOrRoot) {
    const isOwnerOrRoot = checkUserRole("owner") || checkUserRole("root");
    if (!isOwnerOrRoot) {
      return next("/ecommerce/calendar");
    }
  }

  if (
    !to.path.startsWith("/subscription") &&
    !to.path.startsWith("/maintenance") &&
    to.path !== "/settings/company" &&
    to.path !== "/authentication/logout"
    // !rootNumbers.includes(decoded.phoneNumber)
  ) {
    await forcePaymentEnabledCheck();
  }

  return next();
});

export default router;
